import {
  doc,
  writeBatch,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  serverTimestamp,
} from 'firebase/firestore';
import DOMPurify from 'dompurify';
import { db } from '../firebase';
import { getEffectiveTenantId, tenantCollection, tenantPatientCollection, tenantPatientDoc } from './tenantContext';
import {
  FIRST_SETUP_MINUTES_ERROR,
  resolveRpmSessionContextFromPrograms,
  resolveSetupTrainingMinutesCap,
  SETUP_TRAINING_ENCOUNTER_TYPE,
} from '../utils/setupTrainingMinutesPolicy';
import { resolveEncounterProgram } from './userProgramAssignments';
import {
  rpmOutOfServiceInfo,
  isSuppressionEnabled,
  SUPPRESSION_EXEMPT_TYPES,
} from '../utils/rpmServicePolicy';
import { recordDailyWin, wasDailyWinRecorded } from '../utils/dailyWins';

export const MANUAL_ENCOUNTER_TYPES = [
  { value: 'telephone', label: 'Telephone' },
  { value: 'e_message', label: 'E-Message' },
  { value: 'in_person', label: 'In-Person' },
  { value: 'videocall', label: 'Videocall' },
  { value: 'documentation_only', label: 'Documentation Only' },
  { value: 'setup_training', label: 'Setup / Training' },
];

export const AUTOMATED_ENCOUNTER_TYPES = [
  { value: 'patient_message_received', label: 'Patient Message Received' },
  { value: 'care_team_message_sent', label: 'Care Team Message Sent' },
  { value: 'rpm_reading_received', label: 'RPM Reading Received' },
  { value: 'pathway_assigned', label: 'Pathway Assigned' },
  { value: 'pathway_completed', label: 'Pathway Completed' },
  { value: 'pathway_answer', label: 'Pathway Answer' },
  { value: 'education_assigned', label: 'Education Assigned' },
  { value: 'education_completed', label: 'Education Completed' },
  { value: 'care_alert_acknowledged', label: 'Care Alert Acknowledged' },
  { value: 'care_alert_resolved', label: 'Care Alert Resolved' },
  { value: 'message_acknowledged', label: 'Message Acknowledged' },
  { value: 'message_resolved', label: 'Message Resolved' },
  { value: 'program_status_change', label: 'Program Status Change' },
  { value: 'patient_app_reminder', label: 'Patient App Reminder' },
];

export const ENCOUNTER_TYPES = [
  ...MANUAL_ENCOUNTER_TYPES,
  ...AUTOMATED_ENCOUNTER_TYPES,
  { value: 'system', label: 'System (Legacy)' },
];

export const ENCOUNTER_PAGE_SIZE = 200;

export const SESSION_MINUTES_MIN = 0;
export const SESSION_MINUTES_MAX = 180;

/**
 * @param {unknown} raw
 * @returns {number}
 */
export function clampSessionMinutes(raw) {
  const x = typeof raw === 'number' ? raw : parseInt(String(raw ?? '').trim(), 10);
  if (!Number.isFinite(x) || x < SESSION_MINUTES_MIN || x > SESSION_MINUTES_MAX) {
    throw new Error(
      `Session minutes must be a whole number from ${SESSION_MINUTES_MIN} to ${SESSION_MINUTES_MAX}.`
    );
  }
  return Math.round(x);
}

/**
 * Sanitize rich text for storage and display.
 * @param {string} dirty
 * @returns {string}
 */
export function sanitizeEncounterHtml(dirty) {
  return DOMPurify.sanitize(dirty || '', {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'ul', 'ol', 'li', 'span'],
    ALLOWED_ATTR: ['style', 'class'],
  });
}

/**
 * @param {{ id?: string, firstName?: string, lastName?: string, displayName?: string } | null} profile
 */
export function authorFieldsFromProfile(profile) {
  const p = profile || {};
  return {
    enteredByUid: p.id || '',
    enteredByFirstName: String(p.firstName || '').trim(),
    enteredByLastName: String(p.lastName || '').trim(),
    enteredByDisplayName: String(p.displayName || '').trim(),
  };
}

export function formatEncounterAuthorLine(enc) {
  const fn = enc.enteredByFirstName || '';
  const dn = enc.enteredByDisplayName ? ` (${enc.enteredByDisplayName})` : '';
  const ln = enc.enteredByLastName || '';
  const left = `${fn}${dn}`.trim();
  return [left, ln].filter(Boolean).join(' ').trim() || '—';
}

function mapEncounterDoc(d) {
  const data = d.data();
  return {
    id: d.id,
    ...data,
    entryAt: data.entryAt?.toDate?.() ?? data.entryAt,
    editedAt: data.editedAt?.toDate?.() ?? data.editedAt,
    voidedAt: data.voidedAt?.toDate?.() ?? data.voidedAt,
  };
}

function encountersQuery(col, includeAllStatuses, excludeSystemGenerated, startAfterDoc, pageSize) {
  const statusClause = includeAllStatuses
    ? [where('status', 'in', ['active', 'voided'])]
    : [where('status', '==', 'active')];
  const systemClause = excludeSystemGenerated ? [where('isSystemGenerated', '==', false)] : [];
  const base = [...statusClause, ...systemClause, orderBy('entryAt', 'desc')];
  if (startAfterDoc) {
    return query(col, ...base, startAfter(startAfterDoc), limit(pageSize));
  }
  return query(col, ...base, limit(pageSize));
}

/**
 * @param {string} patientId
 * @param {{ includeVoided?: boolean, isAdmin?: boolean, pageSize?: number, excludeSystemGenerated?: boolean, startAfterDoc?: import('firebase/firestore').QueryDocumentSnapshot|null }} opts
 */
export async function listPatientEncounters(patientId, opts = {}) {
  const tenantId = await getEffectiveTenantId();
  const pageSize = opts.pageSize ?? ENCOUNTER_PAGE_SIZE;
  const includeAllStatuses = Boolean(opts.isAdmin && opts.includeVoided);
  const excludeSystemGenerated = Boolean(opts.excludeSystemGenerated);
  const col = tenantPatientCollection(tenantId, patientId, 'patientEncounters');
  const qy = encountersQuery(
    col,
    includeAllStatuses,
    excludeSystemGenerated,
    opts.startAfterDoc || null,
    pageSize
  );
  const snap = await getDocs(qy);
  return {
    encounters: snap.docs.map(mapEncounterDoc),
    lastDoc: snap.docs[snap.docs.length - 1] ?? null,
    hasMore: snap.docs.length === pageSize,
  };
}

/** Max rows fetched per patient for reporting (avoid oversized fan-out reads). */
export const REPORT_ENCOUNTER_QUERY_CAP = 800;

/**
 * Active encounters whose effectiveDate is in [effectiveStart, effectiveEnd] (inclusive ISO YYYY-MM-DD strings).
 */
export async function getActiveEncountersInEffectiveDateRange(
  tenantId,
  patientIds,
  effectiveStart,
  effectiveEnd,
  maxRowsPerPatient = REPORT_ENCOUNTER_QUERY_CAP,
) {
  if (!tenantId || !patientIds?.length || !effectiveStart || !effectiveEnd) return [];
  const lim = Math.min(Math.max(1, maxRowsPerPatient), REPORT_ENCOUNTER_QUERY_CAP);
  const merged = [];
  const promises = patientIds.map(async (patientId) => {
    const col = tenantPatientCollection(tenantId, patientId, 'patientEncounters');
    const qy = query(
      col,
      where('status', '==', 'active'),
      where('effectiveDate', '>=', effectiveStart),
      where('effectiveDate', '<=', effectiveEnd),
      orderBy('effectiveDate', 'desc'),
      limit(lim),
    );
    const snap = await getDocs(qy);
    snap.docs.forEach((d) => {
      merged.push({ patientId: patientId, ...mapEncounterDoc(d) });
    });
  });
  await Promise.all(promises);
  return merged;
}

const PEEK_QUERY_CAP = 200;

/**
 * Active (and optionally voided) encounters for one patient whose effectiveDate
 * is in [effectiveStart, effectiveEnd]. System rows can be dropped client-side.
 * Used by the Billing Review encounter peek.
 *
 * @param {string} patientId
 * @param {{
 *   effectiveStart: string,
 *   effectiveEnd: string,
 *   includeVoided?: boolean,
 *   excludeSystemGenerated?: boolean,
 *   maxRows?: number,
 * }} opts
 * @returns {Promise<{ encounters: object[], truncated: boolean }>}
 */
export async function listEncountersInEffectiveDateRange(patientId, opts = {}) {
  const empty = { encounters: [], truncated: false };
  const tenantId = await getEffectiveTenantId();
  const start = opts.effectiveStart;
  const end = opts.effectiveEnd;
  if (!tenantId || !patientId || !start || !end) return empty;

  const maxRows = Math.min(Math.max(1, opts.maxRows || PEEK_QUERY_CAP), REPORT_ENCOUNTER_QUERY_CAP);
  const col = tenantPatientCollection(tenantId, patientId, 'patientEncounters');

  const fetchStatus = async (status) => {
    const qy = query(
      col,
      where('status', '==', status),
      where('effectiveDate', '>=', start),
      where('effectiveDate', '<=', end),
      orderBy('effectiveDate', 'desc'),
      limit(maxRows),
    );
    const snap = await getDocs(qy);
    return {
      rows: snap.docs.map(mapEncounterDoc),
      truncated: snap.docs.length === maxRows,
    };
  };

  const active = await fetchStatus('active');
  let voided = { rows: [], truncated: false };
  if (opts.includeVoided) {
    voided = await fetchStatus('voided');
  }

  let encounters = [...active.rows, ...voided.rows];
  if (opts.excludeSystemGenerated) {
    encounters = encounters.filter((e) => e.isSystemGenerated !== true);
  }
  encounters.sort((a, b) => {
    const byDate = String(b.effectiveDate || '').localeCompare(String(a.effectiveDate || ''));
    if (byDate) return byDate;
    const at = a.entryAt instanceof Date ? a.entryAt.getTime() : 0;
    const bt = b.entryAt instanceof Date ? b.entryAt.getTime() : 0;
    return bt - at;
  });

  return {
    encounters,
    truncated: active.truncated || voided.truncated,
  };
}


async function loadPatientProgramsForPolicy(tenantId, patientId) {
  const col = tenantPatientCollection(tenantId, patientId, 'patientPrograms');
  const snap = await getDocs(query(col));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * @param {string} patientId
 * @param {{ enrollmentDate: string, dischargeDate?: string|null, excludeEncounterId?: string|null }} opts
 * @returns {Promise<{ id: string, effectiveDate: string }|null>}
 */
export async function getEarliestActiveSetupTrainingInSession(patientId, opts) {
  const tenantId = await getEffectiveTenantId();
  const { enrollmentDate, dischargeDate = null, excludeEncounterId = null } = opts || {};
  if (!tenantId || !patientId || !enrollmentDate) return null;

  const col = tenantPatientCollection(tenantId, patientId, 'patientEncounters');
  const clauses = [
    where('status', '==', 'active'),
    where('encounterType', '==', SETUP_TRAINING_ENCOUNTER_TYPE),
    where('effectiveDate', '>=', enrollmentDate),
    orderBy('effectiveDate', 'asc'),
    limit(5),
  ];
  if (dischargeDate) {
    clauses.splice(3, 0, where('effectiveDate', '<=', dischargeDate));
  }
  const snap = await getDocs(query(col, ...clauses));
  for (const d of snap.docs) {
    if (excludeEncounterId && d.id === excludeEncounterId) continue;
    const data = d.data();
    return { id: d.id, effectiveDate: data.effectiveDate || '' };
  }
  return null;
}

/**
 * @param {object} args
 * @param {string} args.patientId
 * @param {string} args.encounterType
 * @param {string} args.effectiveDate
 * @param {number} args.sessionMinutes
 * @param {boolean} [args.isSystemGenerated]
 * @param {string|null} [args.excludeEncounterId]
 * @param {Array<object>|null} [args.programs]
 */
export async function assessSetupTrainingMinutesPolicy({
  patientId,
  encounterType,
  effectiveDate,
  sessionMinutes,
  isSystemGenerated = false,
  excludeEncounterId = null,
  programs = null,
}) {
  if (encounterType !== SETUP_TRAINING_ENCOUNTER_TYPE || isSystemGenerated) {
    return { maxMinutes: SESSION_MINUTES_MAX, isFirstSetup: false, sessionNumber: null };
  }

  const tenantId = await getEffectiveTenantId();
  const progs = programs ?? (await loadPatientProgramsForPolicy(tenantId, patientId));
  const sessionWindow = resolveRpmSessionContextFromPrograms(progs);
  if (!sessionWindow) {
    return { maxMinutes: SESSION_MINUTES_MAX, isFirstSetup: false, sessionNumber: null };
  }

  const earliestExisting = await getEarliestActiveSetupTrainingInSession(patientId, {
    enrollmentDate: sessionWindow.enrollmentDate,
    dischargeDate: sessionWindow.dischargeDate,
    excludeEncounterId,
  });

  const cap = resolveSetupTrainingMinutesCap({
    encounterType,
    effectiveDate,
    sessionWindow,
    earliestExisting,
    sessionNumber: sessionWindow.sessionNumber,
  });

  if (cap.isFirstSetup && sessionMinutes > 0) {
    throw new Error(FIRST_SETUP_MINUTES_ERROR);
  }

  return cap;
}


/**
 * Out-of-service suppression check for system-generated encounters, behind the
 * tenant flag billingConfig.suppressOutOfServiceMinutes. Fails open (keeps
 * minutes) on any lookup error. Manual encounters are handled by the
 * out-of-service confirmation dialog instead (EncounterLogSection).
 */
async function shouldSuppressSystemMinutes(tenantId, patientId, encounterType, effectiveDate, sessionMinutes) {
  if (sessionMinutes <= 0 || SUPPRESSION_EXEMPT_TYPES.has(encounterType)) return false;
  try {
    const tenantSnap = await getDoc(doc(db, 'tenants', tenantId));
    if (!isSuppressionEnabled(tenantSnap.exists() ? tenantSnap.data() : null)) return false;
    const programs = await loadPatientProgramsForPolicy(tenantId, patientId);
    return rpmOutOfServiceInfo(programs, effectiveDate).outOfService;
  } catch (e) {
    console.warn('Out-of-service suppression check failed open', e?.message || e);
    return false;
  }
}

function auditPayload({ patientId, encounterId, modType, profile }) {
  const a = authorFieldsFromProfile(profile);
  return {
    patientId,
    encounterId,
    modType,
    actorUid: a.enteredByUid,
    actorFirstName: a.enteredByFirstName,
    actorLastName: a.enteredByLastName,
    actorDisplayName: a.enteredByDisplayName,
    at: serverTimestamp(),
  };
}

/**
 * @param {object} input
 * @param {string} input.patientId
 * @param {string} input.bodyHtml - will be sanitized
 * @param {string} input.effectiveDate - YYYY-MM-DD
 * @param {string} [input.effectiveTime] - HH:MM (24-hour)
 * @param {string} input.encounterType
 * @param {object|null} input.profile
 * @param {boolean} [input.isSystemGenerated]
 * @param {string} [input.systemReason]
 * @param {number} input.sessionMinutes
 * @param {boolean} [input.outOfServiceMinutesConfirmed] - user explicitly confirmed
 *   nonzero minutes for a patient out of the RPM program on the effective date
 */
export async function createPatientEncounter(input) {
  const {
    patientId,
    bodyHtml,
    effectiveDate,
    effectiveTime,
    encounterType,
    profile,
    isSystemGenerated = false,
    systemReason = '',
    sessionMinutes: sessionMinutesIn,
    outOfServiceMinutesConfirmed = false,
  } = input;
  const body = sanitizeEncounterHtml(bodyHtml);
  if (!patientId || !effectiveDate || !encounterType) {
    throw new Error('Missing required encounter fields.');
  }
  const author = authorFieldsFromProfile(profile);
  if (!author.enteredByUid) throw new Error('Not signed in.');
  const sessionMinutes = clampSessionMinutes(sessionMinutesIn);

  // Program attribution: which program this care time belongs to, resolved from
  // the entering user's program assignments as of the effective date (RPM default).
  const program = resolveEncounterProgram(profile, null, effectiveDate);

  await assessSetupTrainingMinutesPolicy({
    patientId,
    encounterType,
    effectiveDate,
    sessionMinutes,
    isSystemGenerated,
  });

  const tenantId = await getEffectiveTenantId();

  // Out-of-service suppression for system/auto encounters: discharged/closed
  // RPM patients keep the audit trail but accrue 0 minutes (tenant flag).
  let finalMinutes = sessionMinutes;
  let suppressed = false;
  if (isSystemGenerated) {
    suppressed = await shouldSuppressSystemMinutes(
      tenantId, patientId, encounterType, effectiveDate, sessionMinutes,
    );
    if (suppressed) finalMinutes = 0;
  }

  const encRef = doc(tenantPatientCollection(tenantId, patientId, 'patientEncounters'));
  const auditRef = doc(tenantCollection(tenantId, 'patientEncounterAuditLog'));
  const batch = writeBatch(db);

  const encData = {
    patientId,
    tenantId,
    bodyHtml: body,
    entryAt: serverTimestamp(),
    effectiveDate,
    ...(effectiveTime ? { effectiveTime } : {}),
    encounterType,
    status: 'active',
    ...author,
    isSystemGenerated: Boolean(isSystemGenerated),
    systemReason: isSystemGenerated ? (systemReason || 'pending') : null,
    sessionMinutes: finalMinutes,
    ...(suppressed
      ? {
        minutesSuppressedReason: 'rpm_out_of_service',
        suppressedSessionMinutes: sessionMinutes,
      }
      : {}),
    ...(outOfServiceMinutesConfirmed && !isSystemGenerated
      ? {
        outOfServiceMinutesConfirmed: true,
        outOfServiceConfirmedByUid: author.enteredByUid,
        outOfServiceConfirmedByName:
          author.enteredByDisplayName
          || `${author.enteredByFirstName} ${author.enteredByLastName}`.trim(),
        outOfServiceConfirmedAt: serverTimestamp(),
      }
      : {}),
    programServiceTypeId: program.serviceTypeId,
    programServiceTypeAbbreviation: program.serviceTypeAbbreviation,
  };

  batch.set(encRef, encData);
  batch.set(
    auditRef,
    auditPayload({
      patientId,
      encounterId: encRef.id,
      modType: 'create',
      profile,
    })
  );
  await batch.commit();
  if (!isSystemGenerated && finalMinutes > 0) {
    recordDailyWin({
      uid: author.enteredByUid,
      type: 'careMinutes',
      patientId,
      minutes: finalMinutes,
      eventId: `encounter:${encRef.id}`,
    });
  }

  // billingData totals are reconciled server-side by onEncounterWrittenReconcileBilling
  // (full re-sum of active encounters). No client-side increment: a stray optimistic
  // delta landing after the reconcile write is what inflated totalMinutes.

  return encRef.id;
}

/**
 * @param {string} encounterId
 * @param {string} patientId
 * @param {object} patch
 * @param {string} patch.bodyHtml
 * @param {string} patch.effectiveDate
 * @param {string} patch.encounterType
 * @param {number} patch.sessionMinutes
 * @param {object} profile - admin
 */
export async function updatePatientEncounter(encounterId, patientId, patch, profile) {
  const tenantId = await getEffectiveTenantId();
  const ref = tenantPatientDoc(tenantId, patientId, 'patientEncounters', encounterId);

  const oldSnap = await getDoc(ref);
  const oldData = oldSnap.exists() ? oldSnap.data() : {};

  const body = sanitizeEncounterHtml(patch.bodyHtml ?? '');
  const admin = authorFieldsFromProfile(profile);
  const sessionMinutes = clampSessionMinutes(patch.sessionMinutes);
  const newType = patch.encounterType || '';

  await assessSetupTrainingMinutesPolicy({
    patientId,
    encounterType: newType,
    effectiveDate: patch.effectiveDate,
    sessionMinutes,
    isSystemGenerated: Boolean(oldData.isSystemGenerated),
    excludeEncounterId: encounterId,
  });

  const auditRef = doc(tenantCollection(tenantId, 'patientEncounterAuditLog'));
  const batch = writeBatch(db);
  batch.update(ref, {
    bodyHtml: body,
    effectiveDate: patch.effectiveDate,
    encounterType: patch.encounterType,
    sessionMinutes,
    ...(patch.outOfServiceMinutesConfirmed
      ? {
        outOfServiceMinutesConfirmed: true,
        outOfServiceConfirmedByUid: admin.enteredByUid,
        outOfServiceConfirmedByName:
          admin.enteredByDisplayName
          || `${admin.enteredByFirstName} ${admin.enteredByLastName}`.trim(),
        outOfServiceConfirmedAt: serverTimestamp(),
      }
      : {}),
    editedAt: serverTimestamp(),
    editedByUid: admin.enteredByUid,
    editedByFirstName: admin.enteredByFirstName,
    editedByLastName: admin.enteredByLastName,
    editedByDisplayName: admin.enteredByDisplayName,
  });
  batch.set(
    auditRef,
    auditPayload({
      patientId,
      encounterId,
      modType: 'edit',
      profile,
    })
  );
  await batch.commit();
  const originalWinId = `encounter:${encounterId}`;
  if (
    !oldData.isSystemGenerated
    && oldData.enteredByUid === admin.enteredByUid
    && wasDailyWinRecorded(admin.enteredByUid, originalWinId)
  ) {
    recordDailyWin({
      uid: admin.enteredByUid,
      type: 'careMinutes',
      patientId,
      minutes: sessionMinutes - Number(oldData.sessionMinutes || 0),
      eventId: `encounter-edit:${encounterId}:${Date.now()}`,
    });
  }

  // billingData totals for both the old and new month are reconciled server-side by
  // onEncounterWrittenReconcileBilling. No client-side increment (see createPatientEncounter).
}

/**
 * @param {string} encounterId
 * @param {string} patientId
 * @param {object} profile - admin
 */
export async function voidPatientEncounter(encounterId, patientId, profile) {
  const tenantId = await getEffectiveTenantId();
  const ref = tenantPatientDoc(tenantId, patientId, 'patientEncounters', encounterId);

  const admin = authorFieldsFromProfile(profile);
  const auditRef = doc(tenantCollection(tenantId, 'patientEncounterAuditLog'));
  const batch = writeBatch(db);
  batch.update(ref, {
    status: 'voided',
    voidedAt: serverTimestamp(),
    voidedByUid: admin.enteredByUid,
    voidedByFirstName: admin.enteredByFirstName,
    voidedByLastName: admin.enteredByLastName,
    voidedByDisplayName: admin.enteredByDisplayName,
  });
  batch.set(
    auditRef,
    auditPayload({
      patientId,
      encounterId,
      modType: 'void',
      profile,
    })
  );
  await batch.commit();

  // billingData totals are reconciled server-side by onEncounterWrittenReconcileBilling.
  // No client-side decrement (see createPatientEncounter).
}

/**
 * Format entry / edit / void lines for display (Central-localized dates).
 * @param {Date|import('firebase/firestore').Timestamp} d
 */
export function formatEncounterDisclaimerDate(d) {
  if (!d) return '';
  const date = d?.toDate ? d.toDate() : d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', { timeZone: 'America/Chicago' });
}

export const ENCOUNTER_TYPE_VALUES = ENCOUNTER_TYPES.map((t) => t.value);
